<template>
  <div class="main-page">
    <organization-breadcrumb
      :items="[$t('study_sets.curriculum'), $t('assignments.class_assignment'), currentAssignmentLibrary.name]"
    />
    <div id="page-content">
      <el-row type="flex" justify="center">
        <el-col :xs="24" :md="22">
          <div class="sub-header flex items-center">
            <div class="flex justify-between" style="width: 100%">
              <div>
                <p class="assignment-name">
                  <span @click="$router.go(-1)">
                    <i class="el-icon-back"></i>
                  </span>
                  {{ $t('homework_library.duplicate_title') }}
                </p>
                <el-form :rules="rules" :model="newAssignmentLibrary" class="duplicate-form" ref="firstForm">
                  <el-row class="form-row">
                    <el-col>
                      <elsa-input
                        v-model="newAssignmentLibrary.name"
                        :label="$t('homework.form.assignment_name')"
                        :placeholder="$t('study_sets.form.name_your_assignment')"
                        name="name"
                      />
                    </el-col>
                  </el-row>
                </el-form>
              </div>
            </div>
          </div>
          <section>
            <lesson-sets
              v-if="currentAssignmentLibrary.lesson_sets && currentAssignmentLibrary.lesson_sets.length > 0"
              :data="currentAssignmentLibrary.lesson_sets"
              :loading="loading"
              :remove-node="true"
              v-on:remove-lesson="handelRemoveLesson"
            />
            <study-sets v-else :data="currentAssignmentLibrary.study_sets" :loading="loading" />
          </section>
          <section>
            <div class="base-create-footer flex">
              <tertiary-button @click.native="$router.go(-1)" class="cancel-button">
                {{ $t('common.cancel') }}
              </tertiary-button>
              <primary-button
                v-loading="saveLoading"
                @click.native="handleSave"
                class="save-button"
                :disabled="saveDisabled"
              >
                {{ $t('common.save') }}
              </primary-button>
            </div>
            <api-errors :errors="errors" />
          </section>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import TertiaryButton from '@/components/common/buttons/TertiaryButton';
import PrimaryButton from '@/components/common/buttons/PrimaryButton';
import { format } from 'date-fns';
import OrganizationBreadcrumb from '@/components/common/OrganizationBreadcrumb';
import LessonSets from '@/components/homeworks/details/LessonSets';
import StudySets from '@/components/homeworks/details/StudySets';
import { DATE_FORMAT } from '@/constants/generic';
import ElsaInput from '@/components/common/Input';
import { handleFormValidation } from '@/helpers/formValidationHelper';
import ApiErrors from '@/components/common/ApiErrors';
import { successMessage } from '@/utils/messages';

export default {
  props: {
    assignmentLibraryId: null,
  },
  components: {
    TertiaryButton,
    PrimaryButton,
    OrganizationBreadcrumb,
    LessonSets,
    StudySets,
    ElsaInput,
    ApiErrors,
  },
  computed: {
    ...mapGetters({
      getCurrentClass: 'classes/getCurrentClass',
      currentOrganizationType: 'organization/currentOrganizationType',
    }),
  },
  data() {
    return {
      modals: {
        showConfirmDelete: false,
      },
      currentAssignmentLibrary: {
        name: null,
        created_at: null,
        assigned: [],
        lesson_sets: [],
        study_sets: [],
      },
      newAssignmentLibrary: {
        name: null,
        lesson_original_ids: [],
        study_sets: [],
      },
      rules: {
        name: { required: true, type: 'string', message: this.$t('homework.errors.please_provide_name') },
      },
      loading: true,
      saveLoading: false,
      errors: {},
      saveDisabled: false,
    };
  },
  async mounted() {
    try {
      await this.getAssignmentLibrary();
    } catch (error) {
      this.errors = error;
    }
  },
  methods: {
    ...mapActions({
      setCurrentAssignment: 'homeworks/setCurrentAssignment',
      createAssignmentLibrary: 'homeworks/createAssignmentLibrary',
    }),
    formatDate(date) {
      return date ? format(new Date(date), DATE_FORMAT) : '--';
    },
    formatClassCount(number) {
      if (number > 1) {
        return `${number} ${this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, `groups`))}`;
      } else {
        return `${number} ${this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, `group`))}`;
      }
    },
    handelRemoveLesson(lessons) {
      this.newAssignmentLibrary.lesson_original_ids = lessons;
      this.saveDisabled = this.newAssignmentLibrary.lesson_original_ids.length === 0;
    },
    async getAssignmentLibrary() {
      this.loading = true;
      const params = { type: 'full' };
      const { data } = await this.$axios.get(`/v1/homeworks/${this.assignmentLibraryId}/assignment-library-detail`, {
        params,
      });
      this.currentAssignmentLibrary = data;
      this.newAssignmentLibrary.name = this.$t('common.copy_of') + data.name;
      data.lesson_sets.forEach(({ modules }) => {
        modules.forEach(({ submodules }) => {
          submodules.forEach(({ lessons }) => {
            lessons.forEach((lesson) => this.newAssignmentLibrary.lesson_original_ids.push(lesson.original_id));
          });
        });
      });
      this.newAssignmentLibrary.study_sets = data.study_sets.map((item) => item.id);
      this.loading = false;
    },
    handleSave() {
      handleFormValidation(this.$refs.firstForm, this.validFormCallback, () => {});
    },
    async validFormCallback() {
      try {
        this.saveLoading = true;
        await this.createAssignmentLibrary(this.newAssignmentLibrary);
        this.saveLoading = false;
        const message = this.$t('homework_library.duplicate_success', {
          name: this.currentAssignmentLibrary.name,
        });
        successMessage(message, 'elsa-message success', {
          showClose: false,
        })(this);
        await this.$router.push({
          name: 'assignment-library',
          params: {
            organizationId: this.$store.state.organization.currentOrganizationId,
          },
        });
      } catch (e) {
        this.errors = e;
        this.saveLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.main-page {
  overflow: hidden;
  width: 100%;
}

.assignment-name {
  font-size: 32px;
  line-height: 48px;
  font-weight: 700;
  margin: 40px 0 8px 0px;
}

.assignment-info {
  color: #8e8e93;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  margin-left: 36px;

  + .assignment-info {
    margin-left: 32px;
  }
}

.section-header {
  font-weight: 600;
  font-size: 20px;
  line-height: 20px;
  margin-bottom: 32px;
  text-transform: uppercase;
  letter-spacing: 00.25px;
}

::v-deep .el-form {
  padding-bottom: 0px;
}

::v-deep .assign-to-class .elsa-chip {
  text-transform: unset !important;
}

.duplicate-form {
  padding-top: 40px;
  padding-bottom: 40px;
}

.base-create-footer {
  justify-content: space-between;
  button {
    height: 40px;
    font-size: 16px !important;
  }

  .cancel-button {
    min-width: 160px;
    &::v-deep span {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .save-button {
    min-width: 160px;
    &::v-deep span {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>
